<script lang="ts">
  import type { VanityNumber } from '$lib/sanity/schemas';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import PortableText from '$components/SanityComponents/PortableText/PortableText.svelte';
  import { type ContentSourceMap, type StudioPathLike } from '@sanity/svelte-loader';
  import HeadingSizeDecorator, {
    type HeadingSize
  } from '$components/Common/HeadingSizeDecorator.svelte';
  import { TwistDivider, TwistBox } from '@twist/core';
  import IntersectionObserver from 'svelte-intersection-observer';
  import { quintOut } from 'svelte/easing';
  import { tweened } from 'svelte/motion';
  import { accessibilityPreferences } from '$lib/stores/accessibility';

  interface Props {
    document: VanityNumber;
    sourceMap: ContentSourceMap;
    path?: StudioPathLike;
  }

  let { document, sourceMap, path = [] }: Props = $props();
  let element: HTMLElement | undefined = $state();
  let running = false;

  //animates number from 0 to any value over 3.1 seconds
  const number = tweened(0, {
    delay: 100,
    duration: 2800,
    easing: quintOut
  });

  //triggers once on intersection
  const doCount = () => {
    //"once" on inersection observer doesn't seem to be firing "only once" as advertised - so doing a manual check
    if (!running) {
      running = true;
      $number = document.number;
    }
  };

  let decimalPrecision = $derived.by(() => {
    const numberStr = document.number.toString();
    if (numberStr.includes('.')) {
      return numberStr.split('.')[1].length;
    }
    return 0;
  });

  let rounded = $derived(
    $number.toLocaleString('en-US', {
      minimumFractionDigits: decimalPrecision,
      maximumFractionDigits: decimalPrecision
    })
  );

  let {
    sizeForPrefix,
    sizeForNumber,
    sizeForUnit
  }: { sizeForPrefix: HeadingSize; sizeForNumber: HeadingSize; sizeForUnit: HeadingSize } =
    $derived.by(() => {
      switch (document?.size) {
        case 'small':
          return { sizeForPrefix: 'h5', sizeForNumber: 'h5', sizeForUnit: 'h5' };
        case 'medium':
          return { sizeForPrefix: 'h3', sizeForNumber: 'h3', sizeForUnit: 'h4' };
        case 'large':
          return { sizeForPrefix: 'h2', sizeForNumber: 'h2', sizeForUnit: 'h3' };
        case 'xLarge':
          return { sizeForPrefix: 'h1', sizeForNumber: 'h1', sizeForUnit: 'h3' };
      }
    });
</script>

<IntersectionObserver once {element} on:intersect={doCount}>
  <div bind:this={element}>
    <TwistBox marginTop={toFluidVarInput(10, 15)} marginBottom={toFluidVarInput(10, 15)}>
      <TwistDivider margin={0} thickness="1px" />
    </TwistBox>
    <div class="ban">
      {#if document.prefix}
        <HeadingSizeDecorator size={sizeForPrefix}>{document.prefix}</HeadingSizeDecorator>
      {/if}
      <HeadingSizeDecorator size={sizeForNumber}>
        {#if document.animated && !$accessibilityPreferences.reduceMotion}
          {rounded}
        {:else}
          {document.number.toLocaleString()}
        {/if}
      </HeadingSizeDecorator>
      {#if document.unit}
        <HeadingSizeDecorator size={sizeForUnit}>{document.unit}</HeadingSizeDecorator>
      {/if}
    </div>
    {#if document.description}
      <TwistBox>
        <PortableText {sourceMap} path={[...path, 'body']} body={document.description} />
      </TwistBox>
    {/if}
  </div>
</IntersectionObserver>

<style lang="scss">
  @import 'src/scss/media';
  .ban {
    --theme-font-family: var(--font-family-secondary);
  }
</style>
